import type { ClassificationSummary } from 'generated/graphql';

import { useGetCaseClassification } from './hooks/useGetCaseClassification';
import CircularProgress from '@mui/material/CircularProgress';
import Fieldset from 'common/ui/Fieldset';
import Text from 'common/ui/Text';
import Input from 'common/ui/Input';
import { css } from 'styled-components';
import { Colors, Spacings } from 'styles';
import omit from 'lodash/fp/omit';
import { useMemo } from 'react';
import { telemetryContextFor } from 'modules/Apollo/types';

const disabledInputLabelCss = css`
  color: ${Colors.gray8};
`;

const disabledInputCss = css`
  ${disabledInputLabelCss}
  border-color: ${Colors.gray4};
`;

const fieldsetCss = css`
  margin-bottom: ${Spacings.xsmall}rem;
  width: 100%;
`;

const STUDY_CLASSIFICATIONS_TO_HIDE = ['field_of_view'];

export const transformClassificationName = (classificationName: string): string =>
  classificationName
    // replace underscores with spaces
    .replaceAll('_', ' ')
    // turn words without vowels into uppercase
    .replace(/\b([^aeiou\s]+)\b/g, (char) => char.toUpperCase())
    // capitalize first letter of each word
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const joinClassifications = (
  classifications?: Array<Nullable<ClassificationSummary>>
): string =>
  classifications && classifications.length > 0
    ? classifications.map((classification) => classification?.display_name).join(', ')
    : '';

export interface Props {
  showCurrentCaseClassification?: boolean;
  hangingProtocolClassification?: {
    [key: string]: ClassificationSummary[];
  } | null;
}

export const ClassificationCriteria = ({
  showCurrentCaseClassification = false,
  hangingProtocolClassification,
}: Props) => {
  const { loadingClassification, classification: caseClassification } = useGetCaseClassification({
    context: APOLLO_QUERY_CONTEXT,
  });

  const mainStudyClassification = caseClassification?.[0]?.study_classification;

  const unfilteredCurrentClassification = showCurrentCaseClassification
    ? mainStudyClassification
    : hangingProtocolClassification;

  const currentClassification = useMemo(
    () =>
      omit(
        showCurrentCaseClassification ? STUDY_CLASSIFICATIONS_TO_HIDE : [],
        unfilteredCurrentClassification ?? {}
      ),
    [showCurrentCaseClassification, unfilteredCurrentClassification]
  );

  const classificationFields = useMemo(
    () =>
      Object.entries(currentClassification ?? {})
        .map(([classificationKey, classification]: [any, any]) => {
          const value = joinClassifications(classification);
          return value ? (
            <Fieldset css={fieldsetCss} key={classificationKey}>
              <label htmlFor={classificationKey}>
                <Text css={disabledInputLabelCss}>
                  {transformClassificationName(classificationKey)}
                </Text>
              </label>
              <Input
                id={classificationKey}
                name={classificationKey}
                value={value}
                data-testid={`protocol-field-${classificationKey}`}
                css={disabledInputCss}
                disabled
              />
            </Fieldset>
          ) : null;
        })
        .filter(Boolean),
    [currentClassification]
  );

  if (loadingClassification) {
    return <CircularProgress size="1rem" />;
  }

  if (classificationFields.length === 0) {
    return (
      <Text css="text-align: center" color="secondary">
        No classification available
      </Text>
    );
  }

  return classificationFields;
};

ClassificationCriteria.displayName = 'ClassificationCriteria';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ClassificationCriteria);
